import { Button, Modal, notify } from '@affine/component';
import { AuthService } from '@affine/core/modules/cloud';
import { UserFriendlyError } from '@affine/error';
import type { AccountDeletionPreview } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import { HardBlockView } from './hard-block';
import { StepConfirm } from './step-confirm';
import { StepInfo } from './step-info';
import * as styles from './style.css';

// sl-zvjz: Multi-step Account-Deletion modal.
//
// Flow: Overview → Confirm (including direct data export). If the backend
// preview returns a `blockedReason`, render the HARD-BLOCK view instead.
// Cancel exits at any step. Submit on Confirm calls scheduleAccountDeletion;
// success → notify + redirect /sign-in (Backend invalidated sessions).

type Step = 'info' | 'confirm';

export interface AccountDeletionDialogProps {
  open: boolean;
  email: string;
  onOpenChange: (open: boolean) => void;
  onOpenWorkspaceBackup?: () => void;
}

export const AccountDeletionDialog = ({
  open,
  email,
  onOpenChange,
  onOpenWorkspaceBackup,
}: AccountDeletionDialogProps) => {
  const t = useI18n();
  const authService = useService(AuthService);

  const [preview, setPreview] = useState<AccountDeletionPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('info');
  const [submitting, setSubmitting] = useState(false);

  const reloadPreview = useCallback(async () => {
    setPreviewError(null);
    try {
      const result = await authService.getAccountDeletionPreview();
      setPreview(result);
    } catch (err) {
      const error = UserFriendlyError.fromAny(err);
      setPreviewError(error.message);
    }
  }, [authService]);

  useEffect(() => {
    if (!open) {
      setStep('info');
      setPreview(null);
      setPreviewError(null);
      return;
    }
    void reloadPreview();
  }, [open, reloadPreview]);

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const onSubmit = useCallback(
    async (reason: string | undefined) => {
      setSubmitting(true);
      try {
        await authService.scheduleAccountDeletion(reason);
        notify.success({
          title: t['com.streamlain.setting.account.delete.success.toast']({
            days: String(preview?.gracePeriodDays ?? 30),
          }),
        });
        // Backend nuked all sessions — hard-redirect so the SPA picks up
        // the now-invalid cookie state.
        window.location.assign('/sign-in');
      } catch (err) {
        const error = UserFriendlyError.fromAny(err);
        // sl-zvjz Codex-R3 (F8): structured `extensions.code` comparison —
        // the resolver puts the code there, NOT in the message text. The
        // earlier `error.message.includes(...)` was matching on a string
        // that the message never carries, so the inline HARD-BLOCK panel
        // and ALREADY_SCHEDULED notice never triggered.
        const code = error.code;
        // Owner-of-active-workspace can race the preview (e.g. user accepted
        // a fresh member invite in another tab). Refresh + switch view.
        if (code === 'OWNER_OF_ACTIVE_WORKSPACE') {
          await reloadPreview();
        } else if (code === 'ALREADY_SCHEDULED') {
          notify.warning({
            title:
              t['com.streamlain.setting.account.delete.already-scheduled'](),
          });
          close();
        } else {
          notify.error(error);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [authService, close, preview?.gracePeriodDays, reloadPreview, t]
  );

  if (!open) return null;

  if (previewError) {
    return (
      <Modal open={open} onOpenChange={onOpenChange} width={520}>
        <div className={styles.dialogContent}>
          <p className={styles.errorText}>{previewError}</p>
          <div className={styles.footer}>
            <Button onClick={close}>{t['Close']()}</Button>
          </div>
        </div>
      </Modal>
    );
  }

  if (!preview) {
    // Loading state — match the dialog frame so the height doesn't pop.
    return (
      <Modal open={open} onOpenChange={onOpenChange} width={520}>
        <div className={styles.dialogContent}>
          <p>{t['Loading']()}</p>
        </div>
      </Modal>
    );
  }

  if (preview.blockedReason) {
    return (
      <Modal open={open} onOpenChange={onOpenChange} width={520}>
        <HardBlockView preview={preview} onClose={close} />
      </Modal>
    );
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange} width={520}>
      {step === 'info' ? (
        <StepInfo
          preview={preview}
          onCancel={close}
          onNext={() => setStep('confirm')}
        />
      ) : (
        <StepConfirm
          email={email}
          hasOwnedWorkspaces={
            preview.ownedWorkspaces.length > 0 && !!onOpenWorkspaceBackup
          }
          submitting={submitting}
          onBack={() => setStep('info')}
          onCancel={close}
          onOpenWorkspaceBackup={onOpenWorkspaceBackup}
          onSubmit={onSubmit}
        />
      )}
    </Modal>
  );
};
